import loaderSignal from "signals/Loader.signal";
import alertSignal from "signals/Alert.signal";
import api from "api/api";
import submitApplicationSignal, {
  applicationDraftSignal,
  submitApplicationBusinessSummarySignal,
  submitApplicationErrorsSignal,
  submitApplicationLenderSummarySignal
} from "signals/SubmitApplication.signal";
import { einRegex } from "components/global/Constant/regularExpression";
import userAccountSignal from "../../../signals/UserAccount.signal";
import { ocrSignal } from "../OCR/ocr.signal";
import { uploadFilesToStorage } from "libs/functions/global.functions";
import { classifyOcrIntake, formatOcrDataForSubmit, normalizeOcrData, parseOcrData } from "../OCR/ocr.helpers";
import $appSettings from "signals/AppSettings.signal";
import {
  buildBusinessDataFromOcr,
  buildSupportingDocuments,
  parseCurrencyAmount,
  partitionDraftSupportingDocuments,
  resolveFundingProgramWithDocuments,
} from "./submitApplicationData.helpers";

export const NUM_OF_STEPS = 5;

let lockNotificationListenerWsClient = null;

const isEdoPortal = () => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  return userAccountSignal.value.userData.account.portalType === PORTAL_TYPES.edo;
};

const positiveFundingProgramId = (value) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const draftAlreadyHasFundingProgram = (draft) =>
  !!(draft?.fundingProgramId || draft?.fundingProgram?.id);

const programIdToAttachIfMissing = (draft, fundingProgramId) => {
  if (draftAlreadyHasFundingProgram(draft)) {
    return null;
  }
  return positiveFundingProgramId(fundingProgramId);
};

const getApplicationDraftsWhere = () => {
  const { APPLICATION_STATUS } = $appSettings.value.constants;

  const where = {
    status: APPLICATION_STATUS.inProgress,
    isActive: true,
  };

  if (isEdoPortal()) {
    where.submittedByEdo = true;
  } else {
    where.fundingProgramMembership = {
      lender: {
        accountId: userAccountSignal.value.userData.accountId,
      },
    };
  }

  return where;
};

const fetchFundingProgramWithDocuments = (fundingProgramId) =>
  api.get({
    path: `/fundingPrograms?id=${fundingProgramId}&include[supportingDocuments]=true&include[referenceProgram]=true`,
  });

const mergeFundingProgramSelection = (fetched, selected) => ({
  ...fetched,
  // Preserve nested refs from candidatePrograms if the refetch omits them.
  referenceProgram: fetched?.referenceProgram || selected?.referenceProgram,
  supportingDocuments: fetched?.supportingDocuments?.length
    ? fetched.supportingDocuments
    : selected?.supportingDocuments || [],
});

const updateFundingProgramSelection = async (fundingProgramId, selectedFundingProgram) => {
  if (
    selectedFundingProgram &&
    (!selectedFundingProgram.supportingDocuments || selectedFundingProgram.supportingDocuments.length === 0)
  ) {
    try {
      const fundingProgram = await fetchFundingProgramWithDocuments(fundingProgramId);
      submitApplicationSignal.update({
        fundingProgramId,
        fundingProgram: mergeFundingProgramSelection(fundingProgram, selectedFundingProgram),
      });
    } catch {
      submitApplicationSignal.update({ fundingProgramId, fundingProgram: selectedFundingProgram });
    }
    return;
  }

  submitApplicationSignal.update({ fundingProgramId, fundingProgram: selectedFundingProgram });
};

/** Ignore duplicate OCR WebSocket payloads so we do not re-parse and wipe in-memory edits. */
const isDuplicateOcrResultPayload = (incoming, previousRaw) => {
  if (!incoming || !previousRaw) return false;
  if (!Object.keys(previousRaw).length) return false;
  try {
    return JSON.stringify(previousRaw) === JSON.stringify(incoming);
  } catch {
    return false;
  }
};

const uploadFileAndUpdatePath = async (file, updatePath) => {
  const [signedUrl] = await uploadFilesToStorage([file], 'applicationDocument');
  updatePath(signedUrl);
};

const generateSignedUrlsForAdditionalDocument = async () => {
  submitApplicationSignal.update({ backingUpAdditionalFiles: true });

  const { filesMap, additionalFilesList } = submitApplicationSignal.value;

  for (const [key, fileEntry] of Object.entries(filesMap)) {
    if (fileEntry.filePath) continue;

    await uploadFileAndUpdatePath(fileEntry.file, (signedUrl) => {
      submitApplicationSignal.update({
        filesMap: {
          ...submitApplicationSignal.value.filesMap,
          [key]: {
            ...submitApplicationSignal.value.filesMap[key],
            filePath: signedUrl,
          },
        },
      });
    });
  }

  for (let i = 0; i < additionalFilesList.length; i++) {
    const additionalFile = additionalFilesList[i];
    if (additionalFile.filePath) continue;

    await uploadFileAndUpdatePath(additionalFile.file, (signedUrl) => {
      submitApplicationSignal.update({
        additionalFilesList: additionalFilesList.map((fileBundle, j) =>
          j === i ? { ...fileBundle, filePath: signedUrl } : fileBundle
        ),
      });
    });
  }

  submitApplicationSignal.update({ backingUpAdditionalFiles: false });
};

const buildStep2DraftData = () => {
  const { formattedOcrData, normalizedOcrData } = ocrSignal.value;
  const { fundingProgram } = submitApplicationSignal.value;
  const n = Object.keys(normalizedOcrData || {}).length
    ? normalizedOcrData
    : normalizeOcrData(formattedOcrData || {});

  const data = {
    formData: formattedOcrData,
    businessData: buildBusinessDataFromOcr(n),
    demographics: n.demographics,
  };

  const approvedAmount = parseCurrencyAmount(n.totalLoanAmount);
  if (approvedAmount !== null) {
    data.approvedAmount = approvedAmount;
  }

  const askedAmount = parseCurrencyAmount(n.loanAmountEnrolledInCalcap);
  if (askedAmount !== null) {
    data.askedAmount = askedAmount;
  }

  const businessName = n.businessName;
  const businessEin = n.businessEin || submitApplicationSignal.value.businessEin;
  if (businessName && businessEin) {
    data.businessName = businessName;
    data.businessEinForUpsert = businessEin;
  }

  const applicationConfig = {
    isUsingOcr: submitApplicationSignal.value.isUsingOcr,
    sortedCategories: ocrSignal.value.sortedCategories,
    parsedOcrData: ocrSignal.value.parsedOcrData,
    ocrModelKey: fundingProgram?.ocrModelKey || ocrSignal.value.modelId || null,
    lenderData: submitApplicationLenderSummarySignal.value || {},
  };

  return { data, applicationConfig };
};

export const fetchAndSetFundingPrograms = async () => {
  loaderSignal.update({ isContentLoading: true });
  try {
    const fundingPrograms = await api.get({ path: '/candidatePrograms' });
    return submitApplicationSignal.update({ fundingPrograms });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const setLockNotificationListener = async () => {
  const { url } = await api.post({
    path: '/pubsub',
    body: { data: { hub: 'applicationLocks' } },
  });

  lockNotificationListenerWsClient = new WebSocket(url);
  lockNotificationListenerWsClient.onmessage = (message) => {
    if (!message.data) return;

    const { applicationId, user } = JSON.parse(message.data);
    if (applicationId === applicationDraftSignal.value.selectedDraft?.id) {
      applicationDraftSignal.update({
        removeAccessModalVisible: true,
        removeAccessUser: user,
      });
    }
  };
};

export const tearDownLockNotificationListener = () => {
  if (lockNotificationListenerWsClient) {
    return lockNotificationListenerWsClient.close();
  }
  return null;
};

export const fetchAndSetApplicationDrafts = async (setLoader) => {
  try {
    setLoader && loaderSignal.update({ isContentLoading: true });
    const applicationDrafts = await api.get({
      path: '/applications',
      options: {
        where: getApplicationDraftsWhere(),
        include: {
          fundingProgram: {
            include: {
              supportingDocuments: true,
              referenceProgram: true,
            },
          },
        },
      },
    });

    applicationDraftSignal.update({ applicationDrafts });
  } catch (error) {
    alertSignal.update({
      type: 'alert',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleSelectionChange = (e) => {
  const { name, value } = e.target;

  if (name === 'initialStatus') {
    return submitApplicationSignal.update({ initialStatus: value });
  }

  if (name === 'fundingProgram') {
    const fundingProgramId = parseInt(value, 10);
    const fundingPrograms = submitApplicationSignal.value.fundingPrograms || [];
    const selectedFundingProgram = fundingPrograms.find((mp) => mp.id === fundingProgramId);
    updateFundingProgramSelection(fundingProgramId, selectedFundingProgram);
    return null;
  }

  return null;
};

export const handleInputDirty = (e) => {
  const { name } = e.target;
  return submitApplicationErrorsSignal.update({ [`${name}Dirty`]: true });
};

export const handleInputChange = (e) => {
  const { name, value } = e.target;

  submitApplicationSignal.update({ [name]: value });

  if (name === 'lenderEin' || name === 'businessEin') {
    submitApplicationErrorsSignal.update({
      [name]: !einRegex.test(value),
    });
  }
};

export const handleAttachFile = (name, file) => {
  submitApplicationSignal.update({
    filesMap: {
      ...submitApplicationSignal.value.filesMap,
      [name]: { file, filePath: null, name: file.name, fileType: file.type },
    },
  });
};

export const handleFileCancel = (name) => {
  const { filesMap } = submitApplicationSignal.value;
  const copiedFilesMap = { ...filesMap };
  delete copiedFilesMap[name];

  submitApplicationSignal.update({ filesMap: copiedFilesMap });
};

export const submitApplicationAndNextStep = async () => {
  const { APPLICATION_STATUS } = $appSettings.value.constants;

  loaderSignal.update({
    isContentLoading: true,
    message: 'Creating Application...',
  });
  try {
    const { note, initialStatus, fundingProgramId } = submitApplicationSignal.value;
    const programId = programIdToAttachIfMissing(
      applicationDraftSignal.value.selectedDraft,
      fundingProgramId
    );

    const body = {
      where: { id: applicationDraftSignal.value.selectedDraft.id },
      data: {
        status: APPLICATION_STATUS.pending,
        notes: [],
        action: 'commit',
        ...(programId ? { fundingProgramId: programId } : {}),
      },
    };

    if (isEdoPortal()) {
      body.data.status = initialStatus;
    }

    if (note && note.length > 0) {
      body.data.notes = [{ content: note }];
    }

    const createdApplication = await api.patch({
      path: `/v2/applications/${applicationDraftSignal.value.selectedDraft?.id}`,
      body,
    });

    submitApplicationSignal.update({ createdApplication });
    applicationDraftSignal.update({ selectedDraft: {} });

    return nextStep();
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const nextStep = async () => {
  const { step, fundingProgram } = submitApplicationSignal.value;
  if (step >= NUM_OF_STEPS) return;

  const upcomingStep = step + 1;

  // Programs without OCR (ZE CARB / non-SSBCI etc.): land on digital form, not upload.
  // Create the draft first so digital-form load can save against a real application id.
  // Advance to step 2 before loading the form so the follow-up draft save includes OCR
  // payload. Return early — the trailing handleSaveApplicationDraft(upcomingStep) below
  // re-hydrates an empty applicationConfig and wipes parsedOcrData (Aug 17 walkthrough).
  if (upcomingStep === 2 && fundingProgram && !fundingProgram.ocrModelKey) {
    if (!applicationDraftSignal.value.selectedDraft?.id) {
      await handleSaveApplicationDraft();
    }
    submitApplicationSignal.update({ step: upcomingStep });
    await handleChangeToDigitalForm();
    return;
  }

  if (upcomingStep === 3) {
    const { formattedOcrData } = ocrSignal.value;

    if (!fundingProgram.ocrModelKey) {
      submitApplicationSignal.update({ step: upcomingStep });
      return;
    }

    if (Object.keys(formattedOcrData).length === 0) {
      const { parsedOcrData } = ocrSignal.value;
      if (Object.keys(parsedOcrData).length > 0) {
        formatOcrDataForSubmit();
      }
    }

    if (Object.keys(ocrSignal.value.formattedOcrData).length === 0) {
      alert(
        'Please confirm your application form data before proceeding. Review the fields below your uploaded document, make any edits, and click Confirm. You can upload part of the application and complete the remaining fields here.'
      );
      return;
    }
  }

  if (upcomingStep === 4) {
    await generateSignedUrlsForAdditionalDocument();
  }

  if (step < 4) {
    await handleSaveApplicationDraft(upcomingStep);
    if (!applicationDraftSignal.value.selectedDraft?.id) {
      return;
    }
  }

  submitApplicationSignal.update({ step: upcomingStep });
};

export const saveApplicationDraft = async (nextStep = null) => {
  const { APPLICATION_STATUS, DOCUMENT_TYPES } = $appSettings.value.constants;
  const { step } = submitApplicationSignal.value;

  try {
    const { note, ocrFileType, ocrFileSignedUrl, fundingProgram } = submitApplicationSignal.value;
    const data = {};
    const applicationConfig = {
      step: nextStep ?? step,
    };

    alertSignal.update({
      type: 'notification',
      message: 'Saving draft...',
    });

    if (step === 1) {
      const { initialStatus } = submitApplicationSignal.value;
      applicationConfig.approvedOnCommit = initialStatus === APPLICATION_STATUS.approved;
    }

    if (step === 2) {
      const step2Draft = buildStep2DraftData();
      Object.assign(data, step2Draft.data);
      Object.assign(applicationConfig, step2Draft.applicationConfig);
    }

    if (step === 3) {
      await generateSignedUrlsForAdditionalDocument();
    }

    if (step === 2 || step === 3) {
      const { filesMap, additionalFilesList } = submitApplicationSignal.value;
      data.supportingDocuments = buildSupportingDocuments({
        filesMap,
        additionalFilesList,
        ocrFileType,
        ocrFileSignedUrl,
        fundingProgram,
        applicationTemplateDocType: DOCUMENT_TYPES.applicationTemplate,
      });
    }

    if (step === 4 && note && note.length > 0) {
      data.notes = [{ content: note }];
    }

    const programId = programIdToAttachIfMissing(
      applicationDraftSignal.value.selectedDraft,
      submitApplicationSignal.value.fundingProgramId
    );
    const updatedDraft = await api.patch({
      path: `/v2/applications/${applicationDraftSignal.value.selectedDraft?.id}`,
      body: {
        where: { id: applicationDraftSignal.value.selectedDraft?.id },
        data: {
          ...data,
          applicationConfig,
          action: 'updateDraft',
          ...(programId ? { fundingProgramId: programId } : {}),
        },
      },
    }, 10);

    if (nextStep) {
      hydrateApplicationDataFromDraft(updatedDraft);
    }

    applicationDraftSignal.update({ selectedDraft: updatedDraft });
    await fetchAndSetApplicationDrafts(false);

    alertSignal.update({
      type: 'notification',
      variant: 'success',
      message: 'Saved draft',
    });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  }
};

export const prevStep = () => {
  const { step } = submitApplicationSignal.value;
  if (step > 1) {
    handleSaveApplicationDraft(step - 1);
    submitApplicationSignal.update({ step: step - 1 });
  }
};

// Duplicate-application checks are disabled; resubmissions are allowed after archival.
export const nextStepUnlessApplicationAlreadyExists = async () => {
  submitApplicationSignal.update({ applications: [] });
  return nextStep();
};

export const reset = async () => {
  submitApplicationSignal.update({
    files: [],
    fundingProgramId: '',
    step: 1,
    applicationDocumentType: '',
    applicationDocumentTypes: [],
    note: '',
    filesMap: {},
    ocrModalVisible: false,
    ocrRenderInView: false,
    createdApplication: null,
    additionalFilesList: [],
    ocrFile: null,
    ocrFileName: '',
    ocrFileSignedUrl: '',
    ocrFileType: '',
    isUsingOcr: true,
    ocrIntakeAlert: 'none',
  });

  ocrSignal.reset();
  if (applicationDraftSignal.value.selectedDraft?.id) {
    await handleClearLock();
  }
  applicationDraftSignal.update({ showDraftModal: false });
};

export const exitAndReset = (history) => {
  reset();
  const portal = history.location.pathname.split('/')[1];
  return history.push(`/${portal}`);
};

export const handleAttachFileForOcr = async (name, file, retry = 0) => {
  const { ocrFile, fundingProgram } = submitApplicationSignal.value;
  if (ocrFile) {
    ocrSignal.reset();
  }

  submitApplicationSignal.update({
    ocrFileIsProcessing: true,
    ocrRenderInView: false,
    ocrFile: file,
  });

  let wsClient = null;

  try {
    const { url } = await api.post({
      path: '/pubsub',
      body: { data: { hub: 'ocr' } },
    });

    wsClient = new WebSocket(url);

    if (!fundingProgram.ocrModelKey) {
      return alertSignal.update({
        type: 'notification',
        message:
          'OCR for this program is coming soon. Your application document will still be saved when the application is submitted.',
      });
    }

    const [[signedUrl], [applicationDocumentSignedUrl]] = await Promise.all([
      uploadFilesToStorage([file], 'ocr'),
      uploadFilesToStorage([file], 'applicationDocument'),
    ]);

    submitApplicationSignal.update({
      ocrFileSignedUrl: applicationDocumentSignedUrl,
      ocrFileType: file.type,
      ocrFileName: file.name,
    });

    wsClient.onmessage = async (message) => {
      if (!message.data) return;

      const json = JSON.parse(message.data);
      if (!signedUrl.includes(json.id)) return;

      const incomingOcr = json.data;
      if (isDuplicateOcrResultPayload(incomingOcr, ocrSignal.value.rawOcrData)) {
        wsClient?.close();
        return;
      }

      const intakeKind = classifyOcrIntake(incomingOcr, fundingProgram.ocrModelKey);
      submitApplicationSignal.update({
        ocrFileIsProcessing: false,
        ocrRenderInView: true,
        couldNotProcessOcrAlertVisible: false,
        ocrIntakeAlert: intakeKind === 'complete' ? 'none' : intakeKind,
      });
      wsClient?.close();
      ocrSignal.update({
        rawOcrData: incomingOcr,
        modelId: fundingProgram.ocrModelKey,
      });
      parseOcrData();
      if (Object.keys(ocrSignal.value.parsedOcrData).length > 0) {
        formatOcrDataForSubmit();
      }
      saveApplicationDraft();
    };

    return await api.post({
      path: '/ocr',
      body: {
        data: {
          applicationId: applicationDraftSignal.value.selectedDraft.id,
          url: signedUrl,
          key: fundingProgram.ocrModelKey,
          originalFilename: file.name,
        },
      },
    });
  } catch (error) {
    wsClient?.close();

    if (retry < 10) {
      return setTimeout(() => {
        handleAttachFileForOcr(name, file, retry + 1);
      }, 1000 * Math.pow(1.35, retry + 1));
    }

    submitApplicationSignal.update({ ocrFileIsProcessing: false });

    return alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  }
};

export const navigateToApplication = (history) => {
  const portal = history.location.pathname.split('/')[1];
  const createdApplication = submitApplicationSignal.value.createdApplication;
  reset();

  return history.push(`/${portal}/portfolio/${createdApplication.id}`);
};

export const handleDownloadDocument = async (id) => {
  const downloadUrl = await api.get({
    path: `/uploadPreviews?id=${id}&model=fundingProgramDocuments`,
  });
  window.open(downloadUrl, '_blank');
  return true;
};

export const handleAttachAdditionalFile = (file) => {
  const { additionalFilesList } = submitApplicationSignal.value;
  submitApplicationSignal.update({
    additionalFilesList: additionalFilesList.concat({
      name: file.name,
      file,
      fileType: file.type,
      filePath: null,
    }),
  });
};

export const handleChangeAdditionalFileName = (newName, index) => {
  const { additionalFilesList } = submitApplicationSignal.value;
  return submitApplicationSignal.update({
    additionalFilesList: additionalFilesList.map((fileBundle, i) =>
      index === i ? { ...fileBundle, name: newName } : fileBundle
    ),
  });
};

export const handleCancelAdditionalFile = (index) => {
  const { additionalFilesList } = submitApplicationSignal.value;
  return submitApplicationSignal.update({
    additionalFilesList: additionalFilesList.filter((_, i) => i !== index),
  });
};

export const hydrateApplicationDataFromDraft = (payload) => {
  const { APPLICATION_STATUS, DOCUMENT_TYPES } = $appSettings.value.constants;
  const { selectedDraft } = applicationDraftSignal.value;
  const { step } = submitApplicationSignal.value;

  applicationDraftSignal.update({
    selectedDraft: payload,
    draftPendingRequest: {},
  });

  if (!payload.fundingProgram) {
    alertSignal.update({
      type: 'notification',
      message:
        'This draft cannot be used because the program cannot be found, or it no longer exists.',
    });
    return applicationDraftSignal.update({ showDraftModal: false });
  }

  const { filesMap, additionalFilesList } = partitionDraftSupportingDocuments(
    payload.supportingDocuments,
    payload.fundingProgram,
    DOCUMENT_TYPES.applicationTemplate
  );

  const fundingProgramDocumentId = payload.fundingProgram?.supportingDocuments?.find(
    (sd) => sd.documentType === DOCUMENT_TYPES.applicationTemplate
  )?.id;

  const ocrSupportingDocument = payload?.supportingDocuments.find(
    (sd) => sd.fundingProgramDocumentId === fundingProgramDocumentId
  );

  const fundingProgramToUse = resolveFundingProgramWithDocuments(
    payload.fundingProgram,
    submitApplicationSignal.value.fundingPrograms || []
  );

  const draftConfig = payload.applicationConfig || {};
  const incomingParsed = draftConfig.parsedOcrData;
  const hasIncomingParsed =
    incomingParsed && typeof incomingParsed === 'object' && Object.keys(incomingParsed).length > 0;
  // Do not wipe a live digital/OCR form when draft config has empty OCR snapshots.
  const parsedOcrData = hasIncomingParsed
    ? incomingParsed
    : ocrSignal.value.parsedOcrData || {};
  const formattedOcrData =
    draftConfig.formattedOcrData && Object.keys(draftConfig.formattedOcrData).length > 0
      ? draftConfig.formattedOcrData
      : ocrSignal.value.formattedOcrData || {};
  const normalizedOcrData =
    draftConfig.normalizedOcrData && Object.keys(draftConfig.normalizedOcrData).length > 0
      ? draftConfig.normalizedOcrData
      : ocrSignal.value.normalizedOcrData || {};
  const sortedCategories =
    draftConfig.sortedCategories && Object.keys(draftConfig.sortedCategories || {}).length > 0
      ? draftConfig.sortedCategories
      : ocrSignal.value.sortedCategories || {};

  submitApplicationSignal.update({
    ...submitApplicationSignal.value,
    note: payload.notes[0]?.content || '',
    fundingProgramId: payload.fundingProgramId,
    step: selectedDraft?.id === payload?.id ? step : draftConfig.step || 1,
    fundingProgram: fundingProgramToUse,
    filesMap,
    additionalFilesList,
    ocrFileSignedUrl: ocrSupportingDocument?.filePath,
    ocrFileType: ocrSupportingDocument?.fileType,
    ocrFileName: ocrSupportingDocument?.name,
    ocrFileUuid: ocrSupportingDocument?.uuid,
    initialStatus: payload.approvedOnCommit
      ? APPLICATION_STATUS.approved
      : APPLICATION_STATUS.pending,
    isUsingOcr:
      draftConfig.isUsingOcr !== undefined && draftConfig.isUsingOcr !== null
        ? draftConfig.isUsingOcr
        : submitApplicationSignal.value.isUsingOcr,
  });

  submitApplicationBusinessSummarySignal.update(payload.businessData);

  ocrSignal.update({
    parsedOcrData,
    formattedOcrData,
    normalizedOcrData,
    sortedCategories,
    modelId:
      fundingProgramToUse?.ocrModelKey ||
      draftConfig.ocrModelKey ||
      ocrSignal.value.modelId ||
      '',
  });

  applicationDraftSignal.update({ showDraftModal: false });
};

export const terminateApplicationDraft = async (draft, onSuccess) => {
  loaderSignal.update({ isContentLoading: true });
  try {
    await api.patch({
      path: '/applications',
      body: {
        where: { id: draft.id },
        data: { action: 'deactivate' },
      },
    });
    await fetchAndSetApplicationDrafts();
    applicationDraftSignal.update({
      showDraftModal: false,
      fromEdoPortfolio: false,
      fromLenderPortfolio: false,
      draftPendingRequest: {},
      selectedDraft: {},
      draftModalState: 'chooseDraft',
      removeAccessModalVisible: false,
      removeAccessUser: null,
    });
    if (onSuccess) {
      await onSuccess();
    }
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      error,
      message: error.message,
    });
  } finally {
    loaderSignal.update({ isContentLoading: false });
  }
};

export const handleSaveApplicationDraft = async (nextStep = null) => {
  const { fundingProgramId } = submitApplicationSignal.value;
  if (!positiveFundingProgramId(fundingProgramId)) {
    return alertSignal.update({
      type: 'alert',
      message: 'Please select a funding program to continue.',
    });
  }

  if (applicationDraftSignal.value.selectedDraft?.id) {
    await saveApplicationDraft(nextStep);
  } else {
    await createApplication();
  }
};

const createApplication = async () => {
  const { fundingProgramId, initialStatus } = submitApplicationSignal.value;
  const { APPLICATION_STATUS } = $appSettings.value.constants;
  const programId = positiveFundingProgramId(fundingProgramId);

  if (!programId) {
    return alertSignal.update({
      type: 'alert',
      message: 'Please select a funding program to continue.',
    });
  }

  try {
    loaderSignal.update({ isContentLoading: true });
    const application = await api.post({
      path: '/applications',
      body: {
        data: {
          fundingProgram: { connect: { id: programId } },
          approvedOnCommit: initialStatus === APPLICATION_STATUS.approved,
          applicationConfig: {},
        },
      },
    });

    await fetchAndSetApplicationDrafts();
    applicationDraftSignal.update({ selectedDraft: application });
  } catch (error) {
    alertSignal.update({
      type: 'alert',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleAccessRevoked = async () => {
  applicationDraftSignal.update({
    selectedDraft: {},
    removeAccessModalVisible: false,
  });
  reset();
  await fetchAndSetApplicationDrafts(true);
};

export const handleContinueApplicationDraftClick = async (draft) => {
  if (draft.lockUserId && draft.lockUserId !== userAccountSignal.value.userData.id) {
    applicationDraftSignal.update({
      draftModalState: 'requestAccess',
      draftPendingRequest: draft,
    });
  } else {
    applicationDraftSignal.update({ draftPendingRequest: draft });
    await handleTakeDraftAccess();
  }
};

export const handleTakeDraftAccess = async () => {
  try {
    loaderSignal.update({
      isContentLoading: true,
      message: 'Obtaining access...',
    });

    if (applicationDraftSignal.value.selectedDraft?.id) {
      await handleClearLock();
    }

    const application = await api.patch({
      path: '/applications',
      body: {
        where: { id: applicationDraftSignal.value.draftPendingRequest?.id },
        data: { action: 'grabLock' },
      },
    });

    await fetchAndSetApplicationDrafts();
    hydrateApplicationDataFromDraft(application);
  } catch (error) {
    alertSignal.update({
      type: 'alert',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleClearLock = async () => {
  try {
    loaderSignal.update({ isContentLoading: true });

    await api.patch({
      path: '/applications',
      body: {
        where: { id: applicationDraftSignal.value.selectedDraft?.id },
        data: { action: 'clearLock' },
      },
    });

    applicationDraftSignal.update({ selectedDraft: {} });
  } catch (error) {
    alertSignal.update({
      type: 'alert',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleNavigateToOcrFromApplicationDetailView = async () => {
  await handleTakeDraftAccess();
  submitApplicationSignal.update({ step: 2 });
};

export const fetchAndSetDigitalForm = async () => {
  const { fundingProgram } = submitApplicationSignal.value;
  const referenceProgramKey = fundingProgram?.referenceProgram?.key;

  try {
    loaderSignal.update({ isContentLoading: true });

    if (!referenceProgramKey) {
      throw new Error(
        'Could not load the digital form for this program. Missing reference program.'
      );
    }

    const digitalForm = await api.get({
      path: '/applications/digitalForms',
      options: {
        data: { referenceProgramKey },
      },
    });
    ocrSignal.update({
      rawOcrData: digitalForm,
      parsedOcrData: {},
      formattedOcrData: {},
      normalizedOcrData: {},
      modelId: digitalForm?.docType || '',
    });

    parseOcrData();
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      message: error.message,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const handleChangeToDigitalForm = async () => {
  await fetchAndSetDigitalForm();

  submitApplicationSignal.update({
    isUsingOcr: false,
    applicationFormWarningModalVisible: false,
    ocrFile: null,
    ocrFileSignedUrl: '',
    ocrFileType: '',
    ocrFileName: '',
  });

  if (applicationDraftSignal.value.selectedDraft?.id) {
    await saveApplicationDraft();
  }
};

export const handleChangeToDocumentUpload = () => {
  const { fundingProgram: fp } = submitApplicationSignal.value;
  if (!fp?.ocrModelKey) {
    return alertSignal.update({
      type: 'notification',
      message:
        'OCR upload is not available for this program. Please use the digital form.',
    });
  }
  submitApplicationSignal.update({
    isUsingOcr: true,
    applicationFormWarningModalVisible: false,
  });
  ocrSignal.update({
    parsedOcrData: {},
    rawOcrData: {},
    modelId: fp?.ocrModelKey || '',
    sortedCategories: [],
    formattedOcrData: {},
    normalizedOcrData: {},
  });

  saveApplicationDraft();
};

export const toggleApplicationFormTypeWarningModal = () => {
  const { applicationFormWarningModalVisible } = submitApplicationSignal.value;
  submitApplicationSignal.update({
    applicationFormWarningModalVisible: !applicationFormWarningModalVisible,
  });
};
